<template>
	<wrapper>
		<template v-slot:image>
			<imageBlock />
		</template>

		<template v-slot:form>
			<div class="p-lg-5 p-4">
				<div>
					<h5 class="text-primary">{{ otpSent ? 'Enter OTP' : 'Welcome' }}</h5>
					<p class="text-muted">
						{{
							otpSent && otpSentToEmail
								? 'Please enter the OTP that was sent to ' +
								  formData.contact +
								  ' and your email "' +
								  otpSentToEmail +
								  '"'
								: otpSent && !otpSentToEmail
								? 'Please enter the OTP that was sent to ' + formData.contact
								: 'Sign in with your phone number'
						}}
					</p>
				</div>

				<div class="mt-4">
					<form @submit.prevent="onSubmit">
						<template v-if="!otpSent && !optRetry">
							<div class="mb-3" v-if="formData.scheme">
								<div class="float-end">
									<span class="text-muted text-sm-end">(An OTP will be sent to your phone and email address)</span>
								</div>
								<label for="username" class="form-label">Phone Number</label>
								<input
									v-model="formData.contact"
									type="text"
									class="form-control"
									id="username"
									placeholder="07080672831"
									autocomplete="off"
									maxlength="11"
									@input="allowOnlyNumbers"
								/>
								<div class="text-danger pt-1">
									{{ authStore.authError }}
								</div>
							</div>
							<div class="form-check">
								<input class="form-check-input" type="checkbox" checked id="auth-remember-check" />
								<label class="form-check-label" for="auth-remember-check">This is my device (Remember me)</label>
							</div>
						</template>
						<!-- OTP Template Start -->
						<template v-else>
							<div class="mb-3">
								<label class="form-label" for="password-input">OTP</label>
								<input
									v-model="otp"
									type="text"
									class="form-control"
									placeholder="Enter OTP"
									id="password-input"
									name="password-input"
									required
									@input="allowOnlyNumbers"
								/>
								<div class="text-danger pt-1">
									{{ authStore.authError }}
								</div>
								<p class="pt-1" v-if="otpSent && countDownStarted">
									Didn't receive an OTP? Retry in {{ minutes }}:{{ formattedSeconds }}
								</p>
							</div>
						</template>
						<!-- OTP Template End -->

						<div class="mt-2">
							<button class="btn btn-success w-100 btn-load" type="submit">
								<span class="spinner-grow flex-shrink-0" role="status" v-show="authStore.loading"></span>
								{{ otpSent ? 'Submit OTP' : 'Send OTP' }}
							</button>
							<div class="text-center mt-2" v-if="!otpSent">
								<span class="fw-semibold text-primary text-decoration-underline cursor-pointer" @click="callMe">
									Call Me
								</span>
							</div>
						</div>
						<div class="mt-2 text-center">
							<p v-if="showOTPRetry" class="mb-0">
								Didn't receive OTP?
								<span class="fw-semibold text-primary text-decoration-underline cursor-pointer" @click="resendOTP">
									Resend SMS
								</span>
								or
								<span class="fw-semibold text-primary text-decoration-underline cursor-pointer" @click="callMe">
									Call Me
								</span>
							</p>
							<p v-else class="mb-0">
								Don't have an account ?
								<router-link to="/sign-up" class="fw-semibold text-primary text-decoration-underline">
									Sign Up
								</router-link>
							</p>
						</div>
					</form>
				</div>
			</div>
		</template>

		<template v-slot:footer>
			<SignInFooter />
		</template>
	</wrapper>
</template>

<script setup>
import { ref, reactive, onMounted, watchEffect, computed } from 'vue';
import { notify } from '@kyvg/vue3-notification';
import { useAuthStore, useUtilstStore } from '@/stores';
import { useRoute, useRouter } from 'vue-router';
import { EVENTS, track, getPlans } from '@/helpers';
import wrapper from '@/components/SignIn/wrapper.vue';
import SignInFooter from '@/components/SignIn/footer.vue';
import imageBlock from '@/components/SignIn/imageBlock.vue';
// import Modal from '@/components/Shared/Modal.vue';
// import Tooltip from '@/components/Tooltip.vue';
// import logger from '@/helpers/logger';
import { validateMobile } from '@/helpers/utils';
// import ModalVue from '@/components/Shared/Modal.vue';

const authStore = useAuthStore();
const utilStore = useUtilstStore();
const route = useRoute();
const router = useRouter();
const otpSent = ref(false);
const optRetry = ref(false);
const showOTPRetry = ref(false);
const otpSentToEmail = ref(null);
const otp = ref(); //this is binds to otp input
// Timeout
const totalSeconds = ref(120);
const minutes = ref(2);
const seconds = ref(0);
const countdownInterval = ref(null);
const countDownStarted = ref(false);

const formattedSeconds = computed(() => {
	return seconds.value < 10 ? `0${seconds.value}` : seconds.value;
});

const formData = reactive({
	contact: '',
	scheme: 'SMS'
});

const onSubmit = async () => {
	authStore.authError = '';
	let data;
	if (!otpSent.value) {
		data = {
			mobile: formData.contact,
			scheme: formData.scheme
		};
		if (data.mobile.length < 11) {
			authStore.authError = 'Phone number must be at least 11 digits';
			return;
		}
		getOTP(data);
	} else if (optRetry.value) {
		data = {
			mobile: formData.contact,
			scheme: formData.scheme
		};
		getOTP(data);
		optRetry.value = false;
	} else {
		data = {
			mobile: validateMobile(formData.contact),
			token: otp.value
		};
		try {
			await authStore.login(data);
		} catch (error) {
			notify({
				text: 'There was an error logging you in. Please wait a sec, check your internet connection and try again.',
				type: 'error'
			});
		}
	}
	// authStore.loading = false;
};

const getOTP = (data) => {
	track(EVENTS.REQUEST_OTP, { data: data });
	authStore
		.getOTP(data)
		.then((res) => {
			authStore.loading = false;
			otpSent.value = true;
			otpSentToEmail.value = res.email;
			showOTPRetry.value = false;
			setTimeout(() => {
				document.getElementById('password-input').focus(); //move the focus to OTP input
			}, 0);
			startCountdown();
		})
		.catch((err) => {
			authStore.loading = false;
			try {
				let error = JSON.parse(err);
				if (error.code == '4001' || error.code == '4004') {
					authStore.authError = error.mobile[0];
					return;
				}
			} catch (e) {
				// do nothing
			}
			if (err.message) {
				authStore.authError = err.message;
				return;
			}
			if (
				err.toLowerCase() ==
				'we have sent an otp to your email and mobile number. please wait for 2 minutes before trying again'
			) {
				authStore.authError = err;
				return;
			} else {
				authStore.authError = err;
				notify({
					text: 'Could not get OTP.',
					type: 'warn'
				});
			}
		});
};

const startCountdown = () => {
	countDownStarted.value = true;
	countdownInterval.value = setInterval(() => {
		totalSeconds.value--;
		minutes.value = Math.floor(totalSeconds.value / 60);
		seconds.value = totalSeconds.value % 60;

		if (totalSeconds.value <= 0) {
			clearInterval(countdownInterval.value);
			countDownStarted.value = false;
			totalSeconds.value = 120;
			minutes.value = 2;
			seconds.value = 0;
			showOTPRetry.value = true;
		}
	}, 1000);
};

const allowOnlyNumbers = () => {
	formData.contact = formData.contact.replace(/[^0-9]/g, '');
};

const resendOTP = () => {
	authStore.authError = null;
	formData.scheme = 'SMS';
	optRetry.value = true;
	onSubmit();
};

const callMe = () => {
	if (otpSent.value) {
		optRetry.value = true;
		authStore.authError = null;
	}
	formData.scheme = 'VOICE';
	onSubmit();
};

watchEffect(() => {
	const { plan, coupon } = route.query;
	if (plan && ['1', '2', '3'].includes(plan.toString())) {
		authStore.selectedPlanId = plan;
	}
	if (coupon) {
		authStore.couponCode = coupon;
	}
});

onMounted(async () => {
	authStore.authError = '';
	utilStore.isRecommendedPlanView = false;
	utilStore.isRecommendedPlanMode = false;
	utilStore.nonRecommendedPlanId = null;
	utilStore.signUpMode = false;
	utilStore.signUpFormData = {
		first_name: '',
		last_name: '',
		personal_email: '',
		mobile: ''
	};
	if (authStore.token !== null) {
		router.push('/home');
	}
	if (route.query.mobile_number) {
		formData.contact = route.query.mobile_number;
	}
	try {
		const plans = await getPlans();
		utilStore.setSubscriptionPlans(plans);
	} catch (error) {
		authStore.authError = 'An error occurred. Please refresh.';
	}
	// utilStore.numberPurchaseMode = true;
	// utilStore.isRecommendedPlanView = true;
	// utilStore.isRecommendedPlanMode = true;
});

// onUnmounted(() => {
// 	authStore.authError = '';
// });

track(EVENTS.VIEWED_SIGNIN, { route: route.name });
</script>

<style scoped>
/* Hide the native radio input */
.methods input[type='radio'] {
	appearance: none;
	opacity: 0;
	position: absolute;
}

.methods label {
	position: relative;
	padding: 12px 16px;
	border-radius: 4px;
	outline: 1px solid #d1d6d9;
	margin-bottom: 20px;
}

.methods label p {
	font-size: 12px;
}

.methods label::after {
	content: '';
	position: absolute;
	width: 13px;
	height: 13px;
	border-radius: 50%;
	right: 10px;
	top: 10px;
	background: grey;
}

/* When the radio is checked, style the label accordingly */
.methods input[type='radio']:checked + label {
	outline: solid 1.5px #0e99d2;
}

/* When the radio is focused, style the label accordingly */
/* input[type="radio"]:focus+label {
    outline-offset: 3px;
} */

/* When the radio is checked, style the pseudo-element accordingly */
.methods input[type='radio']:checked + label::after {
	background: #0e99d2;
}
</style>

<script>
export default { name: 'SignInView' };
</script>
